/**
 * Client-side service worker lifecycle owner.
 *
 * Registration, update checks, and shared constants for PWA + Web Push.
 * Do not register `/sw.js` from components directly — use this module.
 */

export const SERVICE_WORKER_SCRIPT_URL = "/sw.js";

/** Default scope for `/sw.js` at site root. */
export const SERVICE_WORKER_SCOPE = "/";

export const SERVICE_WORKER_REGISTRATION_OPTIONS: RegistrationOptions = {
  scope: SERVICE_WORKER_SCOPE,
  updateViaCache: "none",
};

export const SERVICE_WORKER_SKIP_WAITING_MESSAGE = {
  type: "SKIP_WAITING",
} as const;

/** Periodic background update check (production). */
export const SERVICE_WORKER_UPDATE_INTERVAL_MS = 60 * 60 * 1000;

/** Debounce focus / visibility / online update checks. */
export const SERVICE_WORKER_UPDATE_DEBOUNCE_MS = 5 * 60 * 1000;

let lastUpdateCheckAt = 0;

export function isServiceWorkerSupported(): boolean {
  return typeof navigator !== "undefined" && "serviceWorker" in navigator;
}

function registrationScriptUrl(
  registration: ServiceWorkerRegistration,
): string | null {
  const worker =
    registration.active ?? registration.waiting ?? registration.installing;
  return worker?.scriptURL ?? null;
}

/** True when this registration is our `/sw.js` (not Serwist/Workbox leftovers). */
export function isOurServiceWorkerRegistration(
  registration: ServiceWorkerRegistration,
): boolean {
  const scriptUrl = registrationScriptUrl(registration);
  if (!scriptUrl) {
    return false;
  }
  try {
    return new URL(scriptUrl).pathname === SERVICE_WORKER_SCRIPT_URL;
  } catch {
    return scriptUrl.endsWith(SERVICE_WORKER_SCRIPT_URL);
  }
}

async function unregisterStaleServiceWorkers(): Promise<void> {
  if (!isServiceWorkerSupported()) {
    return;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(
    registrations.map(async (registration) => {
      if (isOurServiceWorkerRegistration(registration)) {
        return;
      }
      try {
        await registration.unregister();
      } catch {
        /* non-fatal */
      }
    }),
  );
}

export async function getServiceWorkerRegistration(): Promise<
  ServiceWorkerRegistration | undefined
> {
  if (!isServiceWorkerSupported()) {
    return undefined;
  }

  const scoped =
    await navigator.serviceWorker.getRegistration(SERVICE_WORKER_SCOPE);
  if (scoped && isOurServiceWorkerRegistration(scoped)) {
    return scoped;
  }

  const fallback = await navigator.serviceWorker.getRegistration();
  if (fallback && isOurServiceWorkerRegistration(fallback)) {
    return fallback;
  }

  return undefined;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isServiceWorkerSupported()) {
    return null;
  }

  try {
    await unregisterStaleServiceWorkers();
    return await navigator.serviceWorker.register(
      SERVICE_WORKER_SCRIPT_URL,
      SERVICE_WORKER_REGISTRATION_OPTIONS,
    );
  } catch (error) {
    console.error("ServiceWorker registration failed:", error);
    return null;
  }
}

export async function checkForServiceWorkerUpdate(options?: {
  force?: boolean;
}): Promise<void> {
  if (!isServiceWorkerSupported()) {
    return;
  }

  const now = Date.now();
  if (
    !options?.force &&
    now - lastUpdateCheckAt < SERVICE_WORKER_UPDATE_DEBOUNCE_MS
  ) {
    return;
  }
  lastUpdateCheckAt = now;

  try {
    await unregisterStaleServiceWorkers();
    const registration = await getServiceWorkerRegistration();
    if (registration) {
      await registration.update();
    }
  } catch (error) {
    console.warn("ServiceWorker update check failed:", error);
  }
}

export async function ensureServiceWorkerRegistered(): Promise<ServiceWorkerRegistration> {
  if (!isServiceWorkerSupported()) {
    throw new Error("Service workers are not supported in this context.");
  }

  const existing = await getServiceWorkerRegistration();
  if (existing) {
    void checkForServiceWorkerUpdate({ force: true }).catch(() => {
      /* non-fatal */
    });
    return navigator.serviceWorker.ready;
  }

  const registration = await registerServiceWorker();
  if (!registration) {
    throw new Error("Service worker registration failed.");
  }

  void checkForServiceWorkerUpdate({ force: true }).catch(() => {
    /* non-fatal */
  });

  return navigator.serviceWorker.ready;
}

function defer(callback: () => void): () => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let idleCallbackId: ReturnType<typeof requestIdleCallback> | null = null;
  let loadHandler: (() => void) | null = null;

  const run = () => {
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      idleCallbackId = requestIdleCallback(callback, { timeout: 5000 });
    } else {
      timeoutId = setTimeout(callback, 3000);
    }
  };

  if (document.readyState === "complete") {
    run();
  } else {
    loadHandler = () => run();
    window.addEventListener("load", loadHandler);
  }

  return () => {
    if (loadHandler) {
      window.removeEventListener("load", loadHandler);
    }
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (idleCallbackId) {
      cancelIdleCallback(idleCallbackId);
    }
  };
}

/**
 * Production-only deferred registration + conservative update polling.
 * Returns cleanup for React effects.
 */
export function setupProductionServiceWorkerLifecycle(): () => void {
  if (process.env.NODE_ENV !== "production" || !isServiceWorkerSupported()) {
    return () => {};
  }

  let updateIntervalId: ReturnType<typeof setInterval> | null = null;

  const onWindowFocus = () => {
    void checkForServiceWorkerUpdate();
  };
  const onWindowOnline = () => {
    void checkForServiceWorkerUpdate();
  };
  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      void checkForServiceWorkerUpdate();
    }
  };

  const cancelDefer = defer(() => {
    void registerServiceWorker().then((registration) => {
      if (registration) {
        void checkForServiceWorkerUpdate({ force: true });
      }
    });
  });

  window.addEventListener("focus", onWindowFocus);
  window.addEventListener("online", onWindowOnline);
  document.addEventListener("visibilitychange", onVisibilityChange);
  updateIntervalId = setInterval(() => {
    void checkForServiceWorkerUpdate();
  }, SERVICE_WORKER_UPDATE_INTERVAL_MS);

  return () => {
    cancelDefer();
    window.removeEventListener("focus", onWindowFocus);
    window.removeEventListener("online", onWindowOnline);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    if (updateIntervalId) {
      clearInterval(updateIntervalId);
    }
  };
}

/** @internal */
export function resetServiceWorkerUpdateCheckStateForTests(): void {
  lastUpdateCheckAt = 0;
}
